import { z } from 'zod';
import { moneySchema } from './schemas.js';
import { parseMoneyInput, roundMoney, sumMoney, toMoney } from './money.js';

export const CASHBOOK_ENTRY_TYPES = ['in', 'out'] as const;
export type CashbookEntryType = (typeof CASHBOOK_ENTRY_TYPES)[number];

export const CASHBOOK_VIEW_MODES = ['day', 'month', 'year', 'multi'] as const;
export type CashbookViewMode = (typeof CASHBOOK_VIEW_MODES)[number];

export const CASHBOOK_DEBT_ENTRY_TYPES = ['incurred', 'payment'] as const;
export type CashbookDebtEntryType = (typeof CASHBOOK_DEBT_ENTRY_TYPES)[number];

export const CASHBOOK_DEBT_PAYMENT_CATEGORY = 'debt_payment' as const;
export const CASHBOOK_LOAN_RECEIPT_CATEGORY = 'loan_receipt' as const;

export const CASHBOOK_FUNDING_SOURCES = ['drawer', 'treasury', 'unclassified'] as const;
export type CashbookFundingSource = (typeof CASHBOOK_FUNDING_SOURCES)[number];

/** Funding allowed when creating a new expense (legacy reads may be unclassified). */
export const CASHBOOK_FUNDING_SOURCES_POSTABLE = ['drawer', 'treasury'] as const;
export type CashbookFundingSourcePostable =
  (typeof CASHBOOK_FUNDING_SOURCES_POSTABLE)[number];

export const CASHBOOK_SALES_ORIGINS = ['drawer', 'payvox', 'manual'] as const;
export type CashbookSalesOrigin = (typeof CASHBOOK_SALES_ORIGINS)[number];

export const CASHBOOK_IMPORT_MAX_ROWS = 400;

export function salesDayKeyForDate(date: string): string {
  return `sales:day:${date}`;
}

export function resolveFundingSource(raw: unknown): CashbookFundingSource {
  if (raw === 'drawer' || raw === 'treasury' || raw === 'unclassified') return raw;
  return 'unclassified';
}

export function isPostableFundingSource(
  raw: unknown
): raw is CashbookFundingSourcePostable {
  return raw === 'drawer' || raw === 'treasury';
}

export const PAYVOX_CASHBOOK_MAX_DAYS = 31;

export const PAYVOX_CASHBOOK_MODES = ['daily_total', 'per_receipt'] as const;
export type PayVoxCashbookMode = (typeof PAYVOX_CASHBOOK_MODES)[number];

export const PAYVOX_DAILY_TOTAL_MEMO_EN = 'PayVox net sales';
export const PAYVOX_DAILY_TOTAL_MEMO_AR = 'صافي مبيعات PayVox';

export function payVoxDailyTotalMemo(locale?: string): string {
  return locale?.toLowerCase().startsWith('ar')
    ? PAYVOX_DAILY_TOTAL_MEMO_AR
    : PAYVOX_DAILY_TOTAL_MEMO_EN;
}

export type PayVoxCashbookPullPreviewRow = {
  date: string;
  amount: string;
  memo: string;
  payvoxSaleId?: string;
  payvoxDayKey?: string;
  /** ok = importable; skip = already imported; blocked = manual income exists */
  dayStatus?: 'ok' | 'skip' | 'blocked';
  blockReason?: string;
  drawerExpenses?: string;
  treasuryExpenses?: string;
  unclassifiedExpenses?: string;
  /** Always PayVox gross when status is ok */
  incomeToAdd?: string;
  netAfterExpenses?: string;
};

export type PayVoxCashbookPullPreview = {
  ok: boolean;
  errors: string[];
  rows: PayVoxCashbookPullPreviewRow[];
  /** Days that will be imported (status ok) */
  importableRows?: PayVoxCashbookPullPreviewRow[];
  blockedCount?: number;
  skippedCount: number;
  totalAmount: string;
  saleCount: number;
  /** Where sales were loaded from when preview succeeds. */
  dataSource?: 'finvox' | 'payvox';
  /** FinVox org id used for the lookup. */
  checkedOrgId?: string;
  /** PayVox Firebase project when remote read is configured. */
  remoteProjectId?: string;
};

export const DEFAULT_CASHBOOK_CATEGORIES = [
  { key: 'sales', type: 'in' as const, accountCode: '4000' },
  { key: 'other_income', type: 'in' as const, accountCode: '4000' },
  { key: CASHBOOK_LOAN_RECEIPT_CATEGORY, type: 'in' as const, accountCode: '2000' },
  { key: 'rent', type: 'out' as const, accountCode: '6000' },
  { key: 'utilities', type: 'out' as const, accountCode: '6200' },
  { key: 'salaries', type: 'out' as const, accountCode: '6100' },
  { key: 'supplies', type: 'out' as const, accountCode: '6000' },
  { key: 'other_expense', type: 'out' as const, accountCode: '6000' },
  { key: CASHBOOK_DEBT_PAYMENT_CATEGORY, type: 'out' as const, accountCode: '2000' }
] as const;

export const DEFAULT_CASHBOOK_DEBT_CATEGORIES = [
  { key: 'supplier', labelKey: 'supplier' },
  { key: 'loan', labelKey: 'loan' },
  { key: 'other', labelKey: 'other' }
] as const;

export type CashbookDebtCategory = {
  key: string;
  labelKey: string;
};

export type CashbookCategory = {
  key: string;
  type: CashbookEntryType;
  accountCode: string;
};

const normalizedMoneySchema = z
  .string()
  .transform((s) => parseMoneyInput(s))
  .pipe(moneySchema);

const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

const optionalDebtAmountSchema = z
  .string()
  .optional()
  .transform((s) => (s == null || s.trim() === '' ? '0.00' : parseMoneyInput(s)))
  .pipe(moneySchema);

export const LEGACY_OPENING_DEBT_LINE_ID = 'legacy-opening';
export const OPENING_DEBT_LINE_ID_PREFIX = 'opening:';

export function openingDebtLineEntryId(lineId: string): string {
  return `${OPENING_DEBT_LINE_ID_PREFIX}${lineId}`;
}

export function isOpeningDebtLineEntryId(id: string): boolean {
  return id.startsWith(OPENING_DEBT_LINE_ID_PREFIX);
}

export const cashbookOpeningDebtLineSchema = z.object({
  id: z.string().trim().min(1).max(80),
  creditor: z.string().trim().max(200),
  amount: normalizedMoneySchema,
  categoryKey: z.string().min(1),
  memo: z.string().max(500).optional()
});

export type CashbookOpeningDebtLine = z.infer<typeof cashbookOpeningDebtLineSchema>;

const openingDebtLinesSchema = z
  .array(cashbookOpeningDebtLineSchema)
  .max(100)
  .optional();

export const cashbookOpeningSchema = z.object({
  orgId: z.string().min(1),
  periodId: z.string().min(1),
  amount: normalizedMoneySchema,
  debtAmount: optionalDebtAmountSchema.optional(),
  debtLines: openingDebtLinesSchema,
  date: isoDateSchema
});

export const cashbookUpdateOpeningSchema = z.object({
  orgId: z.string().min(1),
  periodId: z.string().min(1),
  amount: normalizedMoneySchema,
  debtAmount: optionalDebtAmountSchema.optional(),
  debtLines: openingDebtLinesSchema,
  date: isoDateSchema
});

export const cashbookRecordSchema = z
  .object({
    orgId: z.string().min(1),
    periodId: z.string().min(1),
    type: z.enum(CASHBOOK_ENTRY_TYPES),
    amount: z
      .string()
      .transform((s) => parseMoneyInput(s))
      .pipe(moneySchema),
    date: isoDateSchema,
    categoryKey: z.string().min(1),
    memo: z.string().max(500).optional(),
    fundingSource: z.enum(CASHBOOK_FUNDING_SOURCES_POSTABLE).optional()
  })
  .superRefine((data, ctx) => {
    if (data.type === 'out' && !isPostableFundingSource(data.fundingSource)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Funding source is required for expenses',
        path: ['fundingSource']
      });
    }
    if (data.type === 'in' && data.categoryKey === 'sales') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Use Amount in drawer or Pull from PayVox for sales',
        path: ['categoryKey']
      });
    }
    if (data.type === 'in' && data.categoryKey === CASHBOOK_LOAN_RECEIPT_CATEGORY) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Use Add debt with Borrowed money to record loan cash',
        path: ['categoryKey']
      });
    }
  });

export const cashbookClassifyFundingSchema = z.object({
  orgId: z.string().min(1),
  entryId: z.string().min(1),
  fundingSource: z.enum(CASHBOOK_FUNDING_SOURCES_POSTABLE)
});

export const cashbookDrawerCountSchema = z.object({
  orgId: z.string().min(1),
  periodId: z.string().min(1),
  date: isoDateSchema,
  drawerCount: z
    .string()
    .transform((s) => parseMoneyInput(s))
    .pipe(moneySchema),
  memo: z.string().max(500).optional()
});

export const cashbookVoidSchema = z.object({
  orgId: z.string().min(1),
  entryId: z.string().min(1)
});

export const cashbookIncurDebtSchema = z.object({
  orgId: z.string().min(1),
  periodId: z.string().min(1),
  amount: normalizedMoneySchema,
  date: isoDateSchema,
  categoryKey: z.string().min(1),
  creditor: z.string().trim().min(1).max(200),
  memo: z.string().max(500).optional()
});

export const cashbookDebtPaymentAllocationSchema = z.object({
  debtEntryId: z.string().trim().min(1).max(120),
  amount: normalizedMoneySchema
});

export const cashbookPayDebtSchema = z.object({
  orgId: z.string().min(1),
  periodId: z.string().min(1),
  amount: normalizedMoneySchema,
  date: isoDateSchema,
  /** Owner name or {@link UNASSIGNED_DEBT_OWNER_KEY} for opening / unassigned debts. */
  creditor: z.string().trim().min(1).max(200),
  /** Target open debt line id (incurred doc id or `opening:{lineId}`). */
  appliedToDebtEntryId: z.string().trim().min(1).max(120).optional(),
  memo: z.string().max(500).optional(),
  fundingSource: z.enum(CASHBOOK_FUNDING_SOURCES_POSTABLE)
});

/** @deprecated Prefer {@link cashbookPayDebtBulkSchema} — kept for legacy line-targeted batch pays. */
export const cashbookPayDebtLineSchema = z.object({
  appliedToDebtEntryId: z.string().trim().min(1).max(120),
  amount: normalizedMoneySchema
});

/** @deprecated Prefer {@link cashbookPayDebtBulkSchema}. */
export const cashbookPayDebtBatchSchema = z.object({
  orgId: z.string().min(1),
  periodId: z.string().min(1),
  date: isoDateSchema,
  /** Owner name or {@link UNASSIGNED_DEBT_OWNER_KEY}. */
  creditor: z.string().trim().min(1).max(200),
  memo: z.string().max(500).optional(),
  fundingSource: z.enum(CASHBOOK_FUNDING_SOURCES_POSTABLE),
  lines: z.array(cashbookPayDebtLineSchema).min(1).max(50)
});

/** One bulk amount → FIFO oldest-first → one cash out + one debt payment with frozen allocations. */
export const cashbookPayDebtBulkSchema = z.object({
  orgId: z.string().min(1),
  periodId: z.string().min(1),
  amount: normalizedMoneySchema,
  date: isoDateSchema,
  /** Owner name or {@link UNASSIGNED_DEBT_OWNER_KEY}. */
  creditor: z.string().trim().min(1).max(200),
  memo: z.string().max(500).optional(),
  fundingSource: z.enum(CASHBOOK_FUNDING_SOURCES_POSTABLE)
});

export const cashbookVoidDebtSchema = z.object({
  orgId: z.string().min(1),
  entryId: z.string().min(1)
});

export const CASHBOOK_RESET_SCOPES = ['view', 'date', 'opening', 'all'] as const;
export type CashbookResetScope = (typeof CASHBOOK_RESET_SCOPES)[number];

export const cashbookResetSchema = z.discriminatedUnion('scope', [
  z.object({
    scope: z.literal('view'),
    orgId: z.string().min(1),
    startDate: isoDateSchema,
    endDate: isoDateSchema
  }),
  z.object({
    scope: z.literal('date'),
    orgId: z.string().min(1),
    date: isoDateSchema
  }),
  z.object({
    scope: z.literal('opening'),
    orgId: z.string().min(1),
    periodId: z.string().min(1)
  }),
  z.object({
    scope: z.literal('all'),
    orgId: z.string().min(1)
  })
]);

export const cashbookImportRowSchema = z.object({
  type: z.enum(CASHBOOK_ENTRY_TYPES),
  amount: normalizedMoneySchema,
  date: isoDateSchema,
  categoryKey: z.string().min(1),
  memo: z.string().max(500).optional(),
  fundingSource: z.enum(CASHBOOK_FUNDING_SOURCES).optional()
});

export const cashbookImportDryRunSchema = z.object({
  orgId: z.string().min(1),
  periodId: z.string().min(1),
  rows: z.array(cashbookImportRowSchema).min(1).max(CASHBOOK_IMPORT_MAX_ROWS)
});

export const cashbookImportCommitSchema = cashbookImportDryRunSchema;

export const cashbookPayVoxPullSchema = z.object({
  orgId: z.string().min(1),
  periodId: z.string().min(1),
  startDate: isoDateSchema,
  endDate: isoDateSchema,
  mode: z.enum(PAYVOX_CASHBOOK_MODES),
  locale: z.string().optional()
});

export const cashbookSummarySchema = z.object({
  orgId: z.string().min(1),
  startDate: isoDateSchema,
  endDate: isoDateSchema
});

export const cashbookSheetSchema = cashbookSummarySchema.extend({
  viewMode: z.enum(CASHBOOK_VIEW_MODES)
});

export const cashbookViewSchema = cashbookSheetSchema;

export const CASHBOOK_EXPORT_EMAIL_FORMATS = ['csv', 'xlsx', 'pdf'] as const;
export type CashbookExportEmailFormat = (typeof CASHBOOK_EXPORT_EMAIL_FORMATS)[number];

export const cashbookExportEmailSchema = z.object({
  orgId: z.string().min(1),
  format: z.enum(CASHBOOK_EXPORT_EMAIL_FORMATS),
  filename: z.string().min(1).max(200),
  contentBase64: z.string().min(1),
  periodLabel: z.string().min(1).max(200),
  viewMode: z.enum(CASHBOOK_VIEW_MODES)
});

export type CashbookSheetRow = {
  id: string;
  date: string;
  label: string;
  inAmount: string;
  outAmount: string;
  balance: string;
  cashbookEntryId?: string;
  source: 'opening' | 'cashbook';
  voided?: boolean;
  /** Stored note; omit or equal categoryKey means no custom note for display. */
  memo?: string;
  categoryKey?: string;
  fundingSource?: CashbookFundingSource;
  drawerCount?: string;
  salesOrigin?: CashbookSalesOrigin;
};

export type CashbookSheetFooter = {
  totalIn: string;
  totalOut: string;
  net: string;
  endBalance: string;
};

export type CashbookEntryRecord = {
  id: string;
  type: CashbookEntryType;
  amount: string;
  date: string;
  categoryKey: string;
  memo?: string;
  status: 'active' | 'voided';
  /** Present on expenses (`out`); missing on legacy → treat as unclassified */
  fundingSource?: CashbookFundingSource;
  /** Path A: amount accountant counted in the drawer */
  drawerCount?: string;
  /** Stable key for one active sales line per day: `sales:day:YYYY-MM-DD` */
  salesDayKey?: string;
  salesOrigin?: CashbookSalesOrigin;
  source?: string;
  payvoxDayKey?: string;
  payvoxSaleId?: string;
  imported?: boolean;
};

export type CashbookDebtSheetRow = {
  id: string;
  date: string;
  label: string;
  incurredAmount: string;
  paidAmount: string;
  balance: string;
  debtEntryId?: string;
  source: 'opening_debt' | 'debt';
  voided?: boolean;
  memo?: string;
  categoryKey?: string;
};

export type CashbookDebtSheetFooter = {
  totalIncurred: string;
  totalPaid: string;
  net: string;
  endBalance: string;
};

/** Frozen split of a bulk debt payment across open debt lines (pay-time snapshot). */
export type CashbookDebtPaymentAllocation = {
  debtEntryId: string;
  amount: string;
};

export type CashbookDebtEntryRecord = {
  id: string;
  type: CashbookDebtEntryType;
  amount: string;
  date: string;
  categoryKey: string;
  creditor?: string;
  memo?: string;
  status: 'active' | 'voided';
  linkedCashbookEntryId?: string;
  /** When set on payments, allocation applies only to this open debt line (legacy 1:1). */
  appliedToDebtEntryId?: string;
  /** Frozen multi-line split for consolidated bulk payments. */
  allocations?: CashbookDebtPaymentAllocation[];
};

export type CashbookSettings = {
  openingAmount?: string;
  openingDebtAmount?: string;
  openingDebtLines?: CashbookOpeningDebtLine[];
  openingDate?: string;
  openingSetAt?: unknown;
  openingSetBy?: string;
  syncVersion?: unknown;
  categories?: CashbookCategory[];
  /** Org-defined debt kinds beyond supplier / loan / other. */
  debtCategories?: CashbookDebtCategory[];
};

export function categoryByKey(
  key: string,
  overrides: CashbookCategory[] = []
): CashbookCategory | undefined {
  const all = [...overrides, ...DEFAULT_CASHBOOK_CATEGORIES];
  return all.find((c) => c.key === key);
}

/** Default P&L expense code for org-defined out categories. */
export const CUSTOM_CASHBOOK_OUT_ACCOUNT_CODE = '6000';
/** Default P&L income code for org-defined in categories. */
export const CUSTOM_CASHBOOK_IN_ACCOUNT_CODE = '4000';

export function normalizeCashbookCategoryKey(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ');
}

/** Exact key match, then case-insensitive match against overrides + defaults. */
export function findCashbookCategory(
  key: string,
  overrides: CashbookCategory[] = []
): CashbookCategory | undefined {
  const normalized = normalizeCashbookCategoryKey(key);
  if (!normalized) return undefined;
  const exact = categoryByKey(normalized, overrides);
  if (exact) return exact;
  const lower = normalized.toLowerCase();
  const all = [...overrides, ...DEFAULT_CASHBOOK_CATEGORIES];
  return all.find((c) => c.key.toLowerCase() === lower);
}

/** Merge a custom category into settings overrides (skips defaults; case-insensitive). */
export function mergeCashbookCategory(
  overrides: CashbookCategory[],
  next: CashbookCategory
): CashbookCategory[] {
  const key = normalizeCashbookCategoryKey(next.key);
  if (!key) return overrides;
  const lower = key.toLowerCase();
  if (DEFAULT_CASHBOOK_CATEGORIES.some((c) => c.key.toLowerCase() === lower)) {
    return overrides;
  }
  const without = overrides.filter((c) => c.key.toLowerCase() !== lower);
  return [...without, { key, type: next.type, accountCode: next.accountCode }];
}

/** Defaults + custom overrides for a picker (excludes system sales / debt payment / loan receipt / catch-alls). */
export function listSelectableCashbookCategories(
  type: CashbookEntryType,
  overrides: CashbookCategory[] = [],
  options?: {
    excludeSales?: boolean;
    excludeDebtPayment?: boolean;
    excludeLoanReceipt?: boolean;
    excludeOtherExpense?: boolean;
    excludeOtherIncome?: boolean;
  }
): CashbookCategory[] {
  const excludeSales = options?.excludeSales ?? true;
  const excludeDebtPayment = options?.excludeDebtPayment ?? true;
  const excludeLoanReceipt = options?.excludeLoanReceipt ?? true;
  const excludeOtherExpense = options?.excludeOtherExpense ?? true;
  const excludeOtherIncome = options?.excludeOtherIncome ?? true;
  const defaults = DEFAULT_CASHBOOK_CATEGORIES.filter((c) => {
    if (c.type !== type) return false;
    if (excludeDebtPayment && c.key === CASHBOOK_DEBT_PAYMENT_CATEGORY) return false;
    if (excludeLoanReceipt && c.key === CASHBOOK_LOAN_RECEIPT_CATEGORY) return false;
    if (excludeSales && type === 'in' && c.key === 'sales') return false;
    if (excludeOtherExpense && c.key === 'other_expense') return false;
    if (excludeOtherIncome && c.key === 'other_income') return false;
    return true;
  });
  const seen = new Set(defaults.map((c) => c.key.toLowerCase()));
  const extras = overrides
    .filter((c) => {
      if (c.type !== type) return false;
      const lower = c.key.toLowerCase();
      if (seen.has(lower)) return false;
      if (DEFAULT_CASHBOOK_CATEGORIES.some((d) => d.key.toLowerCase() === lower)) return false;
      seen.add(lower);
      return true;
    })
    .sort((a, b) => a.key.localeCompare(b.key, undefined, { sensitivity: 'base' }));
  return [...defaults, ...extras];
}

export function debtCategoryByKey(
  key: string,
  overrides: CashbookDebtCategory[] = []
): CashbookDebtCategory | undefined {
  const normalized = normalizeCashbookCategoryKey(key);
  if (!normalized) return undefined;
  const all = [...overrides, ...DEFAULT_CASHBOOK_DEBT_CATEGORIES];
  const exact = all.find((c) => c.key === normalized);
  if (exact) return exact;
  const lower = normalized.toLowerCase();
  return all.find((c) => c.key.toLowerCase() === lower);
}

/** Merge a custom debt category into settings overrides (skips defaults). */
export function mergeDebtCategory(
  overrides: CashbookDebtCategory[],
  next: CashbookDebtCategory | string
): CashbookDebtCategory[] {
  const key =
    typeof next === 'string'
      ? normalizeCashbookCategoryKey(next)
      : normalizeCashbookCategoryKey(next.key);
  if (!key) return overrides;
  const lower = key.toLowerCase();
  if (DEFAULT_CASHBOOK_DEBT_CATEGORIES.some((c) => c.key.toLowerCase() === lower)) {
    return overrides;
  }
  const without = overrides.filter((c) => c.key.toLowerCase() !== lower);
  return [...without, { key, labelKey: key }];
}

/** Defaults + custom debt kinds for pickers. */
export function listSelectableDebtCategories(
  overrides: CashbookDebtCategory[] = []
): CashbookDebtCategory[] {
  const seen = new Set(DEFAULT_CASHBOOK_DEBT_CATEGORIES.map((c) => c.key.toLowerCase()));
  const extras = overrides
    .filter((c) => {
      const lower = c.key.toLowerCase();
      if (seen.has(lower)) return false;
      if (DEFAULT_CASHBOOK_DEBT_CATEGORIES.some((d) => d.key.toLowerCase() === lower)) return false;
      seen.add(lower);
      return true;
    })
    .sort((a, b) => a.key.localeCompare(b.key, undefined, { sensitivity: 'base' }));
  return [...DEFAULT_CASHBOOK_DEBT_CATEGORIES, ...extras];
}

export function openingDebtAmountOrZero(settings: CashbookSettings): string {
  const lines = settings.openingDebtLines;
  if (lines && lines.length > 0) {
    return sumMoney(lines.map((l) => roundMoney(l.amount)));
  }
  const raw = settings.openingDebtAmount;
  if (!raw) return '0.00';
  return roundMoney(raw);
}

/** Sum of opening debt line amounts (empty → 0.00). */
export function sumOpeningDebtLines(lines: CashbookOpeningDebtLine[]): string {
  if (lines.length === 0) return '0.00';
  return sumMoney(lines.map((l) => roundMoney(l.amount)));
}

/**
 * Normalize opening debt lines for write. New named lines require creditor;
 * legacy blank creditor is allowed only for {@link LEGACY_OPENING_DEBT_LINE_ID}.
 */
export function normalizeOpeningDebtLinesForWrite(
  lines: CashbookOpeningDebtLine[] | undefined,
  debtCategoryOverrides: CashbookDebtCategory[] = []
): CashbookOpeningDebtLine[] {
  if (!lines || lines.length === 0) return [];
  const out: CashbookOpeningDebtLine[] = [];
  for (const line of lines) {
    const amount = roundMoney(line.amount);
    if (toMoney(amount).lte(0)) continue;
    const creditor = line.creditor.trim();
    const isLegacy = line.id === LEGACY_OPENING_DEBT_LINE_ID;
    if (!creditor && !isLegacy) {
      throw new Error('Debt owner is required');
    }
    const categoryKey = normalizeCashbookCategoryKey(line.categoryKey);
    if (!categoryKey) {
      throw new Error('Invalid debt category');
    }
    const known = debtCategoryByKey(categoryKey, debtCategoryOverrides);
    out.push({
      id: line.id.trim(),
      creditor,
      amount,
      categoryKey: known?.key ?? categoryKey,
      ...(line.memo?.trim() ? { memo: line.memo.trim() } : {})
    });
  }
  return out;
}

/**
 * One-time in-memory migration: scalar openingDebtAmount → one legacy line.
 * Returns null when no write is needed.
 */
export function migrateOpeningDebtLinesIfNeeded(
  settings: CashbookSettings
): CashbookSettings | null {
  const existing = settings.openingDebtLines;
  if (existing && existing.length > 0) return null;
  const amount = openingDebtAmountOrZero(settings);
  if (toMoney(amount).lte(0)) {
    if (existing === undefined && settings.openingDebtAmount) {
      return { ...settings, openingDebtLines: [], openingDebtAmount: '0.00' };
    }
    return null;
  }
  const line: CashbookOpeningDebtLine = {
    id: LEGACY_OPENING_DEBT_LINE_ID,
    creditor: '',
    amount,
    categoryKey: 'other'
  };
  return {
    ...settings,
    openingDebtLines: [line],
    openingDebtAmount: amount
  };
}

/** UTC calendar date from the runtime clock (server / ISO string). */
export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Local calendar date (matches browser DatePicker / Calendar). */
export function localTodayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function monthStart(isoDate: string): string {
  return `${isoDate.slice(0, 7)}-01`;
}

export function monthEnd(isoDate: string): string {
  const [y, m] = isoDate.split('-').map(Number);
  const last = new Date(y!, m!, 0).getDate();
  return `${isoDate.slice(0, 7)}-${String(last).padStart(2, '0')}`;
}

export function addDays(iso: string, delta: number): string {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + delta);
  return d.toISOString().slice(0, 10);
}

export function listDaysInRange(start: string, end: string): string[] {
  const out: string[] = [];
  let cur = start;
  while (cur <= end) {
    out.push(cur);
    cur = addDays(cur, 1);
  }
  return out;
}

export function listMonthsInRange(startMonth: string, endMonth: string): string[] {
  const out: string[] = [];
  const [sy, sm] = startMonth.split('-').map(Number);
  const [ey, em] = endMonth.split('-').map(Number);
  let y = sy!;
  let m = sm!;
  while (y < ey! || (y === ey && m <= em!)) {
    out.push(`${y}-${String(m).padStart(2, '0')}`);
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return out;
}

export function assertPayVoxPullDateRange(startDate: string, endDate: string): void {
  if (endDate < startDate) throw new Error('End date must be on or after start date');
  const days = listDaysInRange(startDate, endDate);
  if (days.length > PAYVOX_CASHBOOK_MAX_DAYS) {
    throw new Error(`Date range exceeds ${PAYVOX_CASHBOOK_MAX_DAYS} days`);
  }
}

/** True when cashbook opening setup is complete. */
export function isCashbookOpeningConfigured(settings: CashbookSettings): boolean {
  return Boolean(
    settings.openingDate &&
      settings.openingAmount &&
      toMoney(settings.openingAmount).gt(0)
  );
}

/** Partial opening settings that do not count as configured. */
export function hasStaleCashbookOpeningSettings(settings: CashbookSettings): boolean {
  if (isCashbookOpeningConfigured(settings)) return false;
  return Boolean(settings.openingAmount || settings.openingDate);
}

export type ImportDuplicateKey = {
  date: string;
  type: CashbookEntryType;
  amount: string;
  categoryKey: string;
};

export function importDuplicateKey(row: ImportDuplicateKey): string {
  return `${row.date}|${row.type}|${row.amount}|${row.categoryKey}`;
}

export type ImportDuplicateResult = {
  inFileDuplicateIndices: number[];
  existingMatchIndices: number[];
};

/** In-file duplicates are errors; matches vs existing active entries are warnings. */
export function findCashbookImportDuplicates(
  rows: ImportDuplicateKey[],
  existingActive: ImportDuplicateKey[]
): ImportDuplicateResult {
  const inFileDuplicateIndices: number[] = [];
  const seen = new Map<string, number>();
  for (let i = 0; i < rows.length; i++) {
    const key = importDuplicateKey(rows[i]!);
    const prev = seen.get(key);
    if (prev !== undefined) {
      if (!inFileDuplicateIndices.includes(prev)) inFileDuplicateIndices.push(prev);
      inFileDuplicateIndices.push(i);
    } else {
      seen.set(key, i);
    }
  }

  const existingKeys = new Set(existingActive.map(importDuplicateKey));
  const existingMatchIndices: number[] = [];
  for (let i = 0; i < rows.length; i++) {
    if (existingKeys.has(importDuplicateKey(rows[i]!))) existingMatchIndices.push(i);
  }

  return { inFileDuplicateIndices, existingMatchIndices };
}

export type RawCashMovement = {
  id: string;
  date: string;
  label: string;
  inAmount: string;
  outAmount: string;
  cashbookEntryId?: string;
  source: 'cashbook';
  voided?: boolean;
  sortKey: string;
  memo?: string;
  categoryKey?: string;
  fundingSource?: CashbookFundingSource;
  drawerCount?: string;
  salesOrigin?: CashbookSalesOrigin;
};

/** Custom note for UI/export: hide when missing or equal to categoryKey (legacy fallback). */
export function cashbookCustomNote(
  memo: string | undefined,
  categoryKey: string | undefined
): string | undefined {
  const trimmed = memo?.trim();
  if (!trimmed) return undefined;
  if (categoryKey && trimmed === categoryKey) return undefined;
  return trimmed;
}

export function buildCashbookMovements(entries: CashbookEntryRecord[]): RawCashMovement[] {
  const movements = entries.map((e) => {
    const inAmount = e.type === 'in' ? e.amount : '0.00';
    const outAmount = e.type === 'out' ? e.amount : '0.00';
    const memo = e.memo?.trim() || undefined;
    return {
      id: e.id,
      date: e.date,
      label: e.categoryKey,
      inAmount,
      outAmount,
      cashbookEntryId: e.id,
      source: 'cashbook' as const,
      voided: e.status === 'voided',
      sortKey: `${e.date}#${e.id}`,
      memo,
      categoryKey: e.categoryKey,
      ...(e.type === 'out'
        ? { fundingSource: resolveFundingSource(e.fundingSource) }
        : {}),
      ...(e.drawerCount != null ? { drawerCount: e.drawerCount } : {}),
      ...(e.salesOrigin ? { salesOrigin: e.salesOrigin } : {})
    };
  });
  movements.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  return movements;
}

/** Net active entry amounts from openingDate through asOfDate (inclusive). */
export function netActiveEntriesThrough(
  entries: CashbookEntryRecord[],
  openingDate: string,
  asOfDate: string
): ReturnType<typeof toMoney> {
  let net = toMoney(0);
  for (const e of entries) {
    if (e.status !== 'active') continue;
    if (e.date < openingDate || e.date > asOfDate) continue;
    if (e.type === 'in') net = net.plus(toMoney(e.amount));
    else net = net.minus(toMoney(e.amount));
  }
  return net;
}

/** Cashbook balance at end of asOfDate. */
export function computeCashbookBalance(
  settings: CashbookSettings,
  entries: CashbookEntryRecord[],
  asOfDate: string
): string {
  if (!isCashbookOpeningConfigured(settings)) return '0.00';
  const openingDate = settings.openingDate!;
  if (asOfDate < openingDate) return '0.00';
  let bal = toMoney(settings.openingAmount!);
  bal = bal.plus(netActiveEntriesThrough(entries, openingDate, asOfDate));
  return roundMoney(bal);
}

/** Balance at the start of startDate (before any entries that day). */
export function computeBalanceAtStart(
  settings: CashbookSettings,
  entries: CashbookEntryRecord[],
  startDate: string
): string {
  if (!isCashbookOpeningConfigured(settings)) return '0.00';
  const openingDate = settings.openingDate!;
  if (startDate <= openingDate) {
    if (startDate < openingDate) return '0.00';
    return roundMoney(settings.openingAmount!);
  }
  const dayBefore = addDays(startDate, -1);
  return computeCashbookBalance(settings, entries, dayBefore);
}

export function computeRunningBalance(
  openingBalance: string,
  movements: RawCashMovement[]
): CashbookSheetRow[] {
  let bal = toMoney(openingBalance);
  return movements.map((m) => {
    if (!m.voided) {
      bal = bal.plus(toMoney(m.inAmount)).minus(toMoney(m.outAmount));
    }
    return {
      id: m.id,
      date: m.date,
      label: m.label,
      inAmount: m.inAmount,
      outAmount: m.outAmount,
      balance: roundMoney(bal),
      cashbookEntryId: m.cashbookEntryId,
      source: m.source,
      voided: m.voided,
      memo: m.memo,
      categoryKey: m.categoryKey,
      ...(m.fundingSource ? { fundingSource: m.fundingSource } : {}),
      ...(m.drawerCount != null ? { drawerCount: m.drawerCount } : {}),
      ...(m.salesOrigin ? { salesOrigin: m.salesOrigin } : {})
    };
  });
}

export function aggregateByDay(
  rows: CashbookSheetRow[],
  start: string,
  end: string,
  openingBalance: string
): CashbookSheetRow[] {
  const byDay = new Map<string, { in: string; out: string }>();
  for (const r of rows) {
    if (r.source === 'opening' || r.voided) continue;
    if (r.date < start || r.date > end) continue;
    const prev = byDay.get(r.date) ?? { in: '0.00', out: '0.00' };
    byDay.set(r.date, {
      in: sumMoney([prev.in, r.inAmount]),
      out: sumMoney([prev.out, r.outAmount])
    });
  }

  let preBalance = toMoney(openingBalance);
  for (const r of rows) {
    if (r.source === 'opening' || r.voided) continue;
    if (r.date >= start) break;
    preBalance = preBalance.plus(toMoney(r.inAmount)).minus(toMoney(r.outAmount));
  }

  const days = listDaysInRange(start, end);
  let bal = preBalance;
  return days.map((date) => {
    const agg = byDay.get(date) ?? { in: '0.00', out: '0.00' };
    bal = bal.plus(toMoney(agg.in)).minus(toMoney(agg.out));
    return {
      id: `day-${date}`,
      date,
      label: date,
      inAmount: agg.in,
      outAmount: agg.out,
      balance: roundMoney(bal),
      source: 'cashbook' as const
    };
  });
}

export function aggregateByMonth(
  rows: CashbookSheetRow[],
  startMonth: string,
  endMonth: string,
  openingBalance: string
): CashbookSheetRow[] {
  const months = listMonthsInRange(startMonth, endMonth);
  const byMonth = new Map<string, { in: string; out: string }>();
  for (const r of rows) {
    if (r.source === 'opening' || r.voided) continue;
    const key = r.date.slice(0, 7);
    const prev = byMonth.get(key) ?? { in: '0.00', out: '0.00' };
    byMonth.set(key, {
      in: sumMoney([prev.in, r.inAmount]),
      out: sumMoney([prev.out, r.outAmount])
    });
  }

  const rangeStart = `${startMonth}-01`;
  let preBalance = toMoney(openingBalance);
  for (const r of rows) {
    if (r.source === 'opening' || r.voided) continue;
    if (r.date >= rangeStart) break;
    preBalance = preBalance.plus(toMoney(r.inAmount)).minus(toMoney(r.outAmount));
  }

  let bal = preBalance;
  return months.map((month) => {
    const agg = byMonth.get(month) ?? { in: '0.00', out: '0.00' };
    bal = bal.plus(toMoney(agg.in)).minus(toMoney(agg.out));
    return {
      id: `month-${month}`,
      date: `${month}-01`,
      label: month,
      inAmount: agg.in,
      outAmount: agg.out,
      balance: roundMoney(bal),
      source: 'cashbook' as const
    };
  });
}

export function computeCashbookPeriodTotals(
  movements: RawCashMovement[],
  options: { startDate: string; endDate: string }
): { periodIn: string; periodOut: string; periodNet: string } {
  const inRange = movements.filter(
    (m) => m.date >= options.startDate && m.date <= options.endDate && !m.voided
  );
  let periodIn = toMoney(0);
  let periodOut = toMoney(0);
  for (const m of inRange) {
    periodIn = periodIn.plus(toMoney(m.inAmount));
    periodOut = periodOut.plus(toMoney(m.outAmount));
  }
  return {
    periodIn: roundMoney(periodIn),
    periodOut: roundMoney(periodOut),
    periodNet: roundMoney(periodIn.minus(periodOut))
  };
}

export function sheetFooter(
  rows: CashbookSheetRow[],
  endBalance: string,
  options?: { excludeVoided?: boolean }
): CashbookSheetFooter {
  const excludeVoided = options?.excludeVoided ?? true;
  const dataRows = rows.filter((r) => {
    if (r.source === 'opening') return false;
    if (excludeVoided && r.voided) return false;
    return true;
  });
  const totalIn = sumMoney(dataRows.map((r) => r.inAmount));
  const totalOut = sumMoney(dataRows.map((r) => r.outAmount));
  return {
    totalIn,
    totalOut,
    net: roundMoney(toMoney(totalIn).minus(toMoney(totalOut))),
    endBalance
  };
}

export function sheetToCsv(
  headers: string[],
  rows: string[][],
  footer?: string[]
): string {
  const escape = (v: string) => {
    if (/[",\n]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
    return v;
  };
  const lines = [headers.map(escape).join(',')];
  for (const row of rows) lines.push(row.map(escape).join(','));
  if (footer?.length) lines.push(footer.map(escape).join(','));
  return `\uFEFF${lines.join('\n')}`;
}

/** Max span for multi-month view (months). */
export const CASHBOOK_MAX_VIEW_MONTHS = 24;

export function assertViewRangeMonths(startDate: string, endDate: string): void {
  const startMonth = startDate.slice(0, 7);
  const endMonth = endDate.slice(0, 7);
  const months = listMonthsInRange(startMonth, endMonth);
  if (months.length > CASHBOOK_MAX_VIEW_MONTHS) {
    throw new Error(`Date range exceeds ${CASHBOOK_MAX_VIEW_MONTHS} months`);
  }
}

export type RawDebtMovement = {
  id: string;
  date: string;
  label: string;
  incurredAmount: string;
  paidAmount: string;
  debtEntryId?: string;
  source: 'debt';
  voided?: boolean;
  sortKey: string;
  memo?: string;
  categoryKey?: string;
};

export function buildDebtMovements(entries: CashbookDebtEntryRecord[]): RawDebtMovement[] {
  const movements = entries.map((e) => {
    const incurredAmount = e.type === 'incurred' ? e.amount : '0.00';
    const paidAmount = e.type === 'payment' ? e.amount : '0.00';
    const creditor = e.creditor?.trim();
    const memo = e.memo?.trim() || undefined;
    const label = creditor || e.categoryKey;
    return {
      id: e.id,
      date: e.date,
      label,
      incurredAmount,
      paidAmount,
      debtEntryId: e.id,
      source: 'debt' as const,
      voided: e.status === 'voided',
      sortKey: `${e.date}#${e.id}`,
      memo,
      categoryKey: e.categoryKey
    };
  });
  movements.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  return movements;
}

export function netActiveDebtThrough(
  entries: CashbookDebtEntryRecord[],
  openingDate: string,
  asOfDate: string
): ReturnType<typeof toMoney> {
  let net = toMoney(0);
  for (const e of entries) {
    if (e.status !== 'active') continue;
    if (e.date < openingDate || e.date > asOfDate) continue;
    if (e.type === 'incurred') net = net.plus(toMoney(e.amount));
    else net = net.minus(toMoney(e.amount));
  }
  return net;
}

export function computeTotalDebts(
  settings: CashbookSettings,
  entries: CashbookDebtEntryRecord[],
  asOfDate: string
): string {
  if (!isCashbookOpeningConfigured(settings)) return '0.00';
  const openingDate = settings.openingDate!;
  if (asOfDate < openingDate) return '0.00';
  let bal = toMoney(openingDebtAmountOrZero(settings));
  bal = bal.plus(netActiveDebtThrough(entries, openingDate, asOfDate));
  return roundMoney(bal);
}

export function computeDebtBalanceAtStart(
  settings: CashbookSettings,
  entries: CashbookDebtEntryRecord[],
  startDate: string
): string {
  if (!isCashbookOpeningConfigured(settings)) return '0.00';
  const openingDate = settings.openingDate!;
  if (startDate <= openingDate) {
    if (startDate < openingDate) return '0.00';
    return openingDebtAmountOrZero(settings);
  }
  const dayBefore = addDays(startDate, -1);
  return computeTotalDebts(settings, entries, dayBefore);
}

export function computeDebtRunningBalance(
  openingDebtBalance: string,
  movements: RawDebtMovement[]
): CashbookDebtSheetRow[] {
  let bal = toMoney(openingDebtBalance);
  return movements.map((m) => {
    if (!m.voided) {
      bal = bal.plus(toMoney(m.incurredAmount)).minus(toMoney(m.paidAmount));
    }
    return {
      id: m.id,
      date: m.date,
      label: m.label,
      incurredAmount: m.incurredAmount,
      paidAmount: m.paidAmount,
      balance: roundMoney(bal),
      debtEntryId: m.debtEntryId,
      source: m.source,
      voided: m.voided,
      memo: m.memo,
      categoryKey: m.categoryKey
    };
  });
}

export function aggregateDebtByDay(
  rows: CashbookDebtSheetRow[],
  start: string,
  end: string,
  openingBalance: string
): CashbookDebtSheetRow[] {
  const byDay = new Map<string, { incurred: string; paid: string }>();
  for (const r of rows) {
    if (r.source === 'opening_debt' || r.voided) continue;
    if (r.date < start || r.date > end) continue;
    const prev = byDay.get(r.date) ?? { incurred: '0.00', paid: '0.00' };
    byDay.set(r.date, {
      incurred: sumMoney([prev.incurred, r.incurredAmount]),
      paid: sumMoney([prev.paid, r.paidAmount])
    });
  }

  let preBalance = toMoney(openingBalance);
  for (const r of rows) {
    if (r.source === 'opening_debt' || r.voided) continue;
    if (r.date >= start) break;
    preBalance = preBalance.plus(toMoney(r.incurredAmount)).minus(toMoney(r.paidAmount));
  }

  const days = listDaysInRange(start, end);
  let bal = preBalance;
  return days.map((date) => {
    const agg = byDay.get(date) ?? { incurred: '0.00', paid: '0.00' };
    bal = bal.plus(toMoney(agg.incurred)).minus(toMoney(agg.paid));
    return {
      id: `debt-day-${date}`,
      date,
      label: date,
      incurredAmount: agg.incurred,
      paidAmount: agg.paid,
      balance: roundMoney(bal),
      source: 'debt' as const
    };
  });
}

export function aggregateDebtByMonth(
  rows: CashbookDebtSheetRow[],
  startMonth: string,
  endMonth: string,
  openingBalance: string
): CashbookDebtSheetRow[] {
  const months = listMonthsInRange(startMonth, endMonth);
  const byMonth = new Map<string, { incurred: string; paid: string }>();
  for (const r of rows) {
    if (r.source === 'opening_debt' || r.voided) continue;
    const key = r.date.slice(0, 7);
    const prev = byMonth.get(key) ?? { incurred: '0.00', paid: '0.00' };
    byMonth.set(key, {
      incurred: sumMoney([prev.incurred, r.incurredAmount]),
      paid: sumMoney([prev.paid, r.paidAmount])
    });
  }

  const rangeStart = `${startMonth}-01`;
  let preBalance = toMoney(openingBalance);
  for (const r of rows) {
    if (r.source === 'opening_debt' || r.voided) continue;
    if (r.date >= rangeStart) break;
    preBalance = preBalance.plus(toMoney(r.incurredAmount)).minus(toMoney(r.paidAmount));
  }

  let bal = preBalance;
  return months.map((month) => {
    const agg = byMonth.get(month) ?? { incurred: '0.00', paid: '0.00' };
    bal = bal.plus(toMoney(agg.incurred)).minus(toMoney(agg.paid));
    return {
      id: `debt-month-${month}`,
      date: `${month}-01`,
      label: month,
      incurredAmount: agg.incurred,
      paidAmount: agg.paid,
      balance: roundMoney(bal),
      source: 'debt' as const
    };
  });
}

export function computeDebtPeriodTotals(
  movements: RawDebtMovement[],
  options: { startDate: string; endDate: string }
): { periodDebtIncurred: string; periodDebtPaid: string; periodDebtNet: string } {
  const inRange = movements.filter(
    (m) => m.date >= options.startDate && m.date <= options.endDate && !m.voided
  );
  let incurred = toMoney(0);
  let paid = toMoney(0);
  for (const m of inRange) {
    incurred = incurred.plus(toMoney(m.incurredAmount));
    paid = paid.plus(toMoney(m.paidAmount));
  }
  return {
    periodDebtIncurred: roundMoney(incurred),
    periodDebtPaid: roundMoney(paid),
    periodDebtNet: roundMoney(incurred.minus(paid))
  };
}

export function debtSheetFooter(
  rows: CashbookDebtSheetRow[],
  endBalance: string,
  options?: { excludeVoided?: boolean }
): CashbookDebtSheetFooter {
  const excludeVoided = options?.excludeVoided ?? true;
  const dataRows = rows.filter((r) => {
    if (r.source === 'opening_debt') return false;
    if (excludeVoided && r.voided) return false;
    return true;
  });
  const totalIncurred = sumMoney(dataRows.map((r) => r.incurredAmount));
  const totalPaid = sumMoney(dataRows.map((r) => r.paidAmount));
  return {
    totalIncurred,
    totalPaid,
    net: roundMoney(toMoney(totalIncurred).minus(toMoney(totalPaid))),
    endBalance
  };
}

export function computeNetPosition(treasury: string, totalDebts: string): string {
  return roundMoney(toMoney(treasury).minus(toMoney(totalDebts)));
}

export function assertDebtPaymentAllowed(totalDebts: string, amount: string): void {
  if (toMoney(amount).lte(0)) throw new Error('Amount must be positive');
  if (toMoney(amount).gt(toMoney(totalDebts))) {
    throw new Error('Payment exceeds outstanding debts');
  }
}

/** True when an active payment targets this debt line (legacy id or allocations[]). */
export function debtLineHasCoveringPayments(
  entries: CashbookDebtEntryRecord[],
  debtEntryId: string
): boolean {
  return entries.some((e) => {
    if (e.status !== 'active' || e.type !== 'payment') return false;
    if (e.appliedToDebtEntryId === debtEntryId) return true;
    return (e.allocations ?? []).some((a) => a.debtEntryId === debtEntryId);
  });
}

export function assertCanVoidDebtIncurred(
  settings: CashbookSettings,
  entries: CashbookDebtEntryRecord[],
  entry: CashbookDebtEntryRecord,
  asOfDate: string
): void {
  if (entry.type !== 'incurred') return;
  if (debtLineHasCoveringPayments(entries, entry.id)) {
    throw new Error('Cannot void — payments already recorded against this debt');
  }
  const totalDebts = computeTotalDebts(settings, entries, asOfDate);
  if (toMoney(entry.amount).gt(toMoney(totalDebts))) {
    throw new Error('Cannot void — payments already recorded against this debt');
  }
}

/**
 * Recorded left-in-drawer (sum of drawerCounts) for a day, or null if none recorded.
 */
export function recordedLeftInDrawerForDay(
  entries: CashbookEntryRecord[],
  date: string
): string | null {
  const drawerSales = findActiveDrawerSalesEntriesForDay(entries, date);
  if (drawerSales.length === 0) return null;
  let sum = toMoney(0);
  let any = false;
  for (const s of drawerSales) {
    if (s.drawerCount == null || s.drawerCount === '') continue;
    any = true;
    sum = sum.plus(toMoney(s.drawerCount));
  }
  return any ? roundMoney(sum) : null;
}

/** Drawer funding cannot exceed recorded left-in-drawer when a count exists. Treasury is unrestricted. */
export function assertDrawerFundingAllowsPayment(
  fundingSource: CashbookFundingSourcePostable,
  amount: string,
  leftInDrawer: string | null
): void {
  if (fundingSource !== 'drawer') return;
  if (leftInDrawer == null) return;
  if (toMoney(amount).gt(toMoney(leftInDrawer))) {
    throw new Error('Payment exceeds left in drawer');
  }
}

export function isDebtPaymentCategory(categoryKey: string): boolean {
  return categoryKey === CASHBOOK_DEBT_PAYMENT_CATEGORY;
}

export function isLoanReceiptCategory(categoryKey: string): boolean {
  return categoryKey === CASHBOOK_LOAN_RECEIPT_CATEGORY;
}

/** Sentinel owner key for opening debt and legacy unassigned incurred / payments. */
export const UNASSIGNED_DEBT_OWNER_KEY = '__unassigned__';

export function isUnassignedDebtOwnerKey(ownerKey: string): boolean {
  return ownerKey === UNASSIGNED_DEBT_OWNER_KEY;
}

export function normalizeDebtOwnerName(creditor: string | undefined): string | undefined {
  const trimmed = creditor?.trim();
  return trimmed || undefined;
}

export function debtOwnerKeysMatch(creditor: string | undefined, ownerKey: string): boolean {
  if (isUnassignedDebtOwnerKey(ownerKey)) {
    return !normalizeDebtOwnerName(creditor);
  }
  const name = normalizeDebtOwnerName(creditor);
  if (!name) return false;
  return name.toLowerCase() === ownerKey.toLowerCase();
}

export function ownerKeyFromCreditor(creditor: string | undefined): string {
  return normalizeDebtOwnerName(creditor) ?? UNASSIGNED_DEBT_OWNER_KEY;
}

export function creditorForPayDebtOwnerKey(ownerKey: string): string | undefined {
  if (isUnassignedDebtOwnerKey(ownerKey)) return undefined;
  return ownerKey;
}

export function collectDebtOwners(
  entries: CashbookDebtEntryRecord[],
  settings?: CashbookSettings
): string[] {
  const seen = new Map<string, string>();
  for (const e of entries) {
    if (e.type !== 'incurred') continue;
    const name = normalizeDebtOwnerName(e.creditor);
    if (!name) continue;
    const lower = name.toLowerCase();
    if (!seen.has(lower)) seen.set(lower, name);
  }
  if (settings?.openingDebtLines) {
    for (const line of settings.openingDebtLines) {
      const name = normalizeDebtOwnerName(line.creditor);
      if (!name) continue;
      const lower = name.toLowerCase();
      if (!seen.has(lower)) seen.set(lower, name);
    }
  }
  return Array.from(seen.values()).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' })
  );
}

export function mergeDebtOwner(registry: string[], newOwner: string): string[] {
  const trimmed = normalizeDebtOwnerName(newOwner);
  if (!trimmed) return registry;
  const lower = trimmed.toLowerCase();
  if (registry.some((o) => o.toLowerCase() === lower)) return registry;
  return [...registry, trimmed].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' })
  );
}

export type OpenDebtRow = {
  id: string;
  ownerKey: string;
  date: string;
  categoryKey: string;
  memo?: string;
  originalAmount: string;
  remainingAmount: string;
  source: 'opening_debt' | 'incurred';
};

/**
 * Apply a bulk payment amount to open rows oldest-first (FIFO). Rows must already be age-sorted.
 */
export function allocateBulkPaymentFifo(
  openRows: OpenDebtRow[],
  amount: string
): CashbookDebtPaymentAllocation[] {
  const totalRemaining =
    openRows.length === 0 ? '0.00' : sumMoney(openRows.map((r) => r.remainingAmount));
  assertDebtPaymentAllowed(totalRemaining, amount);

  let left = toMoney(amount);
  const out: CashbookDebtPaymentAllocation[] = [];
  for (const row of openRows) {
    if (left.lte(0)) break;
    const rem = toMoney(row.remainingAmount);
    if (rem.lte(0)) continue;
    const applied = left.lt(rem) ? left : rem;
    out.push({ debtEntryId: row.id, amount: roundMoney(applied) });
    left = left.minus(applied);
  }
  if (left.gt(0)) {
    throw new Error('Payment exceeds outstanding debts');
  }
  return out;
}

export function sumOpenDebtRowTotals(rows: OpenDebtRow[]): {
  originalAmount: string;
  remainingAmount: string;
} {
  if (rows.length === 0) {
    return { originalAmount: '0.00', remainingAmount: '0.00' };
  }
  return {
    originalAmount: sumMoney(rows.map((r) => r.originalAmount)),
    remainingAmount: sumMoney(rows.map((r) => r.remainingAmount))
  };
}

type InternalDebtLine = {
  id: string;
  ownerKey: string;
  date: string;
  amount: string;
  categoryKey: string;
  memo?: string;
  source: 'opening_debt' | 'incurred';
  sortKey: string;
};

type DebtPaymentAllocation = {
  amount: string;
  date: string;
  id: string;
  /** Named owner payment target; null = legacy payment applied oldest-first across all lines. */
  ownerKey: string | null;
  /** When set, payment applies only to this line id (no spill). Legacy 1:1. */
  appliedToDebtEntryId?: string;
  /** Frozen multi-line allocations (consolidated bulk pay). */
  allocations?: CashbookDebtPaymentAllocation[];
  sortKey: string;
};

function activeDebtEntriesThrough(
  entries: CashbookDebtEntryRecord[],
  openingDate: string,
  asOfDate: string
): CashbookDebtEntryRecord[] {
  return entries.filter(
    (e) => e.status === 'active' && e.date >= openingDate && e.date <= asOfDate
  );
}

function buildAllDebtLines(
  settings: CashbookSettings,
  entries: CashbookDebtEntryRecord[],
  openingDate: string,
  asOfDate: string
): InternalDebtLine[] {
  const lines: InternalDebtLine[] = [];

  if (asOfDate >= openingDate) {
    const openingLines = settings.openingDebtLines;
    if (openingLines && openingLines.length > 0) {
      for (const line of openingLines) {
        const amount = roundMoney(line.amount);
        if (toMoney(amount).lte(0)) continue;
        const entryId = openingDebtLineEntryId(line.id);
        lines.push({
          id: entryId,
          ownerKey: ownerKeyFromCreditor(line.creditor || undefined),
          date: openingDate,
          amount,
          categoryKey: line.categoryKey || 'other',
          memo: line.memo,
          source: 'opening_debt',
          sortKey: `${openingDate}#${entryId}`
        });
      }
    } else {
      // Pre-migration fallback: single unassigned synthetic line from scalar.
      const openingAmt = openingDebtAmountOrZero(settings);
      if (toMoney(openingAmt).gt(0)) {
        const entryId = openingDebtLineEntryId(LEGACY_OPENING_DEBT_LINE_ID);
        lines.push({
          id: entryId,
          ownerKey: UNASSIGNED_DEBT_OWNER_KEY,
          date: openingDate,
          amount: openingAmt,
          categoryKey: 'other',
          source: 'opening_debt',
          sortKey: `${openingDate}#${entryId}`
        });
      }
    }
  }

  for (const e of activeDebtEntriesThrough(entries, openingDate, asOfDate)) {
    if (e.type !== 'incurred') continue;
    lines.push({
      id: e.id,
      ownerKey: ownerKeyFromCreditor(e.creditor),
      date: e.date,
      amount: e.amount,
      categoryKey: e.categoryKey,
      memo: e.memo,
      source: 'incurred',
      sortKey: `${e.date}#${e.id}`
    });
  }

  lines.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  return lines;
}

function buildActiveDebtPayments(
  entries: CashbookDebtEntryRecord[],
  openingDate: string,
  asOfDate: string
): DebtPaymentAllocation[] {
  const payments: DebtPaymentAllocation[] = [];
  for (const e of activeDebtEntriesThrough(entries, openingDate, asOfDate)) {
    if (e.type !== 'payment') continue;
    payments.push({
      amount: e.amount,
      date: e.date,
      id: e.id,
      ownerKey: normalizeDebtOwnerName(e.creditor) ?? null,
      appliedToDebtEntryId: e.appliedToDebtEntryId,
      allocations: e.allocations,
      sortKey: `${e.date}#${e.id}`
    });
  }
  payments.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  return payments;
}

function applyAmountToLine(
  remaining: Map<string, string>,
  lineId: string,
  amount: ReturnType<typeof toMoney>
): void {
  if (amount.lte(0)) return;
  const lineRem = toMoney(remaining.get(lineId) ?? '0');
  if (lineRem.lte(0)) return;
  const applied = amount.lt(lineRem) ? amount : lineRem;
  remaining.set(lineId, roundMoney(lineRem.minus(applied)));
}

function allocateDebtPayments(
  lines: InternalDebtLine[],
  payments: DebtPaymentAllocation[]
): Map<string, string> {
  const remaining = new Map<string, string>();
  for (const line of lines) {
    remaining.set(line.id, line.amount);
  }

  for (const pay of payments) {
    let left = toMoney(pay.amount);
    if (left.lte(0)) continue;

    if (pay.allocations && pay.allocations.length > 0) {
      for (const alloc of pay.allocations) {
        applyAmountToLine(remaining, alloc.debtEntryId, toMoney(alloc.amount));
      }
      continue;
    }

    if (pay.appliedToDebtEntryId) {
      applyAmountToLine(remaining, pay.appliedToDebtEntryId, left);
      continue;
    }

    const targets =
      pay.ownerKey === null
        ? lines
        : lines.filter((line) => debtOwnerKeysMatch(pay.ownerKey ?? undefined, line.ownerKey));

    for (const line of targets) {
      if (left.lte(0)) break;
      const lineRem = toMoney(remaining.get(line.id) ?? '0');
      if (lineRem.lte(0)) continue;
      const applied = left.lt(lineRem) ? left : lineRem;
      remaining.set(line.id, roundMoney(lineRem.minus(applied)));
      left = left.minus(applied);
    }
  }

  return remaining;
}

function openDebtRowsFromAllocation(
  lines: InternalDebtLine[],
  remainingMap: Map<string, string>
): OpenDebtRow[] {
  const rows: OpenDebtRow[] = [];
  for (const line of lines) {
    const rem = remainingMap.get(line.id) ?? '0.00';
    if (toMoney(rem).lte(0)) continue;
    rows.push({
      id: line.id,
      ownerKey: line.ownerKey,
      date: line.date,
      categoryKey: line.categoryKey,
      memo: line.memo,
      originalAmount: line.amount,
      remainingAmount: rem,
      source: line.source
    });
  }
  return rows;
}

function ownerKeysMatch(a: string, b: string): boolean {
  return debtOwnerKeysMatch(isUnassignedDebtOwnerKey(a) ? undefined : a, b);
}

export function computeOpenDebtRows(
  settings: CashbookSettings,
  entries: CashbookDebtEntryRecord[],
  asOfDate: string
): OpenDebtRow[] {
  if (!isCashbookOpeningConfigured(settings)) return [];
  const openingDate = settings.openingDate!;
  if (asOfDate < openingDate) return [];

  const lines = buildAllDebtLines(settings, entries, openingDate, asOfDate);
  if (lines.length === 0) return [];

  const payments = buildActiveDebtPayments(entries, openingDate, asOfDate);
  const remainingMap = allocateDebtPayments(lines, payments);
  return openDebtRowsFromAllocation(lines, remainingMap);
}

export function computeOpenDebtRowsForOwner(
  settings: CashbookSettings,
  entries: CashbookDebtEntryRecord[],
  asOfDate: string,
  ownerKey: string
): OpenDebtRow[] {
  return computeOpenDebtRows(settings, entries, asOfDate).filter((row) =>
    ownerKeysMatch(ownerKey, row.ownerKey)
  );
}

export function computeOwnerRemainingBalance(
  settings: CashbookSettings,
  entries: CashbookDebtEntryRecord[],
  asOfDate: string,
  ownerKey: string
): string {
  const rows = computeOpenDebtRowsForOwner(settings, entries, asOfDate, ownerKey);
  if (rows.length === 0) return '0.00';
  return sumMoney(rows.map((r) => r.remainingAmount));
}

export function ownersWithOpenBalance(
  settings: CashbookSettings,
  entries: CashbookDebtEntryRecord[],
  asOfDate: string
): string[] {
  if (!isCashbookOpeningConfigured(settings)) return [];

  const keys = new Set<string>();
  for (const row of computeOpenDebtRows(settings, entries, asOfDate)) {
    keys.add(row.ownerKey);
  }

  return Array.from(keys).sort((a, b) => {
    if (isUnassignedDebtOwnerKey(a)) return -1;
    if (isUnassignedDebtOwnerKey(b)) return 1;
    return a.localeCompare(b, undefined, { sensitivity: 'base' });
  });
}

export function assertLineDebtPaymentAllowed(lineRemaining: string, amount: string): void {
  assertDebtPaymentAllowed(lineRemaining, amount);
}

export type OwnerDebtHistoryKind = 'opening' | 'incurred' | 'payment';

export type OwnerDebtHistoryAllocation = {
  debtEntryId: string;
  amount: string;
  /** Present when the target line still exists; omitted for orphan/removed targets. */
  categoryKey?: string;
  date?: string;
  memo?: string;
  source?: 'opening_debt' | 'incurred';
  missing?: boolean;
};

export type OwnerDebtHistoryRow = {
  id: string;
  date: string;
  kind: OwnerDebtHistoryKind;
  categoryKey: string;
  memo?: string;
  /** Positive for opening/incurred, negative for payment. */
  signedAmount: string;
  runningRemaining: string;
  /** Frozen payment breakdown when the payment used allocations[] or a single target line. */
  allocations?: OwnerDebtHistoryAllocation[];
};

/**
 * Full debt timeline for one owner (opening + incurred + payments), with running remaining.
 */
export function computeOwnerDebtHistory(
  settings: CashbookSettings,
  entries: CashbookDebtEntryRecord[],
  asOfDate: string,
  ownerKey: string
): OwnerDebtHistoryRow[] {
  if (!isCashbookOpeningConfigured(settings)) return [];
  const openingDate = settings.openingDate!;
  if (asOfDate < openingDate) return [];

  const allLines = buildAllDebtLines(settings, entries, openingDate, asOfDate);
  const lineById = new Map(allLines.map((l) => [l.id, l]));
  const lines = allLines.filter((line) => ownerKeysMatch(ownerKey, line.ownerKey));
  const lineIds = new Set(lines.map((l) => l.id));

  type Event = {
    id: string;
    date: string;
    sortKey: string;
    kind: OwnerDebtHistoryKind;
    categoryKey: string;
    memo?: string;
    signedAmount: string;
    allocations?: OwnerDebtHistoryAllocation[];
  };

  function historyAllocationsForPayment(
    e: CashbookDebtEntryRecord
  ): OwnerDebtHistoryAllocation[] | undefined {
    const raw: CashbookDebtPaymentAllocation[] =
      e.allocations && e.allocations.length > 0
        ? e.allocations
        : e.appliedToDebtEntryId
          ? [{ debtEntryId: e.appliedToDebtEntryId, amount: e.amount }]
          : [];
    if (raw.length === 0) return undefined;
    return raw.map((a) => {
      const line = lineById.get(a.debtEntryId);
      if (!line) {
        return { debtEntryId: a.debtEntryId, amount: a.amount, missing: true };
      }
      return {
        debtEntryId: a.debtEntryId,
        amount: a.amount,
        categoryKey: line.categoryKey,
        date: line.date,
        memo: line.memo,
        source: line.source
      };
    });
  }

  const events: Event[] = [];
  for (const line of lines) {
    events.push({
      id: line.id,
      date: line.date,
      sortKey: `${line.date}#0#${line.id}`,
      kind: line.source === 'opening_debt' ? 'opening' : 'incurred',
      categoryKey: line.categoryKey,
      memo: line.memo,
      signedAmount: line.amount
    });
  }

  for (const e of activeDebtEntriesThrough(entries, openingDate, asOfDate)) {
    if (e.type !== 'payment') continue;
    const payOwnerKey = ownerKeyFromCreditor(e.creditor);
    const matchesOwner = ownerKeysMatch(ownerKey, payOwnerKey);
    const targetsThisOwnerLine =
      (e.appliedToDebtEntryId != null && lineIds.has(e.appliedToDebtEntryId)) ||
      (e.allocations ?? []).some((a) => lineIds.has(a.debtEntryId));
    if (!matchesOwner && !targetsThisOwnerLine) continue;
    // Legacy null-creditor payments: only attribute to unassigned owner view unless targeted.
    if (
      normalizeDebtOwnerName(e.creditor) == null &&
      !targetsThisOwnerLine &&
      !isUnassignedDebtOwnerKey(ownerKey)
    ) {
      continue;
    }
    events.push({
      id: e.id,
      date: e.date,
      sortKey: `${e.date}#1#${e.id}`,
      kind: 'payment',
      categoryKey: e.categoryKey,
      memo: e.memo,
      signedAmount: roundMoney(toMoney(e.amount).negated()),
      allocations: historyAllocationsForPayment(e)
    });
  }

  events.sort((a, b) => a.sortKey.localeCompare(b.sortKey));

  let running = toMoney('0');
  const rows: OwnerDebtHistoryRow[] = [];
  for (const ev of events) {
    running = running.plus(toMoney(ev.signedAmount));
    rows.push({
      id: ev.id,
      date: ev.date,
      kind: ev.kind,
      categoryKey: ev.categoryKey,
      memo: ev.memo,
      signedAmount: ev.signedAmount,
      runningRemaining: roundMoney(running),
      ...(ev.allocations && ev.allocations.length > 0 ? { allocations: ev.allocations } : {})
    });
  }
  return rows;
}

/**
 * Whether Unassigned should appear in owner pickers / lists.
 * Only while the unassigned opening (or unassigned lines) still have remaining balance.
 */
export function shouldShowUnassignedDebtOwner(
  settings: CashbookSettings,
  entries: CashbookDebtEntryRecord[],
  asOfDate: string
): boolean {
  if (!isCashbookOpeningConfigured(settings)) return false;
  const remaining = computeOwnerRemainingBalance(
    settings,
    entries,
    asOfDate,
    UNASSIGNED_DEBT_OWNER_KEY
  );
  return toMoney(remaining).gt(0);
}

/** Owners for history dropdown: named owners with any activity + Unassigned while still open. */
export function ownersWithDebtHistory(
  settings: CashbookSettings,
  entries: CashbookDebtEntryRecord[],
  asOfDate: string
): string[] {
  if (!isCashbookOpeningConfigured(settings)) return [];
  const named = collectDebtOwners(entries, settings);
  // Also include named owners who only have payments / fully paid incurred (still in history).
  const seen = new Map<string, string>(named.map((n) => [n.toLowerCase(), n]));
  for (const e of entries) {
    if (e.status !== 'active') continue;
    const name = normalizeDebtOwnerName(e.creditor);
    if (!name) continue;
    const lower = name.toLowerCase();
    if (!seen.has(lower)) seen.set(lower, name);
  }
  const keys = Array.from(seen.values()).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' })
  );
  if (shouldShowUnassignedDebtOwner(settings, entries, asOfDate)) {
    keys.unshift(UNASSIGNED_DEBT_OWNER_KEY);
  }
  return keys;
}

/** Effective funding for an out entry (legacy missing → unclassified). */
export function entryFundingSource(entry: CashbookEntryRecord): CashbookFundingSource {
  if (entry.type !== 'out') return 'unclassified';
  return resolveFundingSource(entry.fundingSource);
}

export function isActivePayVoxSalesEntry(entry: CashbookEntryRecord): boolean {
  return (
    entry.status === 'active' &&
    entry.type === 'in' &&
    (entry.source === 'payvox' || entry.salesOrigin === 'payvox')
  );
}

/** Active Path A (drawer) sales lines for a day, stable-sorted by id. */
export function findActiveDrawerSalesEntriesForDay(
  entries: CashbookEntryRecord[],
  date: string
): CashbookEntryRecord[] {
  return entries
    .filter((e) => {
      if (e.status !== 'active' || e.type !== 'in' || e.date !== date) return false;
      if (isActivePayVoxSalesEntry(e)) return false;
      return e.salesOrigin === 'drawer' || e.drawerCount != null;
    })
    .sort((a, b) => a.id.localeCompare(b.id));
}

export function isActiveSalesDayEntry(entry: CashbookEntryRecord, date: string): boolean {
  if (entry.status !== 'active' || entry.type !== 'in') return false;
  const key = salesDayKeyForDate(date);
  if (entry.salesDayKey === key) return true;
  if (entry.payvoxDayKey === `payvox:day:${date}`) return true;
  return entry.date === date && entry.categoryKey === 'sales' && entry.status === 'active';
}

export function findActiveSalesEntryForDay(
  entries: CashbookEntryRecord[],
  date: string
): CashbookEntryRecord | undefined {
  const key = salesDayKeyForDate(date);
  const byKey = entries.find(
    (e) => e.status === 'active' && e.type === 'in' && e.salesDayKey === key
  );
  if (byKey) return byKey;
  const byPayvox = entries.find(
    (e) =>
      e.status === 'active' &&
      e.type === 'in' &&
      (e.payvoxDayKey === `payvox:day:${date}` || e.salesOrigin === 'payvox') &&
      e.date === date
  );
  if (byPayvox) return byPayvox;
  return entries.find(
    (e) =>
      e.status === 'active' &&
      e.type === 'in' &&
      e.date === date &&
      e.categoryKey === 'sales'
  );
}

/** Any active income that is not a PayVox sales line for that day. */
export function hasManualIncomeOnDay(entries: CashbookEntryRecord[], date: string): boolean {
  return entries.some((e) => {
    if (e.status !== 'active' || e.type !== 'in' || e.date !== date) return false;
    if (isActivePayVoxSalesEntry(e) && e.date === date) return false;
    return true;
  });
}

export function sumExpensesByFundingForDay(
  entries: CashbookEntryRecord[],
  date: string
): { drawer: string; treasury: string; unclassified: string; total: string } {
  let drawer = toMoney('0');
  let treasury = toMoney('0');
  let unclassified = toMoney('0');
  for (const e of entries) {
    if (e.status !== 'active' || e.type !== 'out' || e.date !== date) continue;
    const amt = toMoney(e.amount);
    const src = entryFundingSource(e);
    if (src === 'drawer') drawer = drawer.plus(amt);
    else if (src === 'treasury') treasury = treasury.plus(amt);
    else unclassified = unclassified.plus(amt);
  }
  const total = drawer.plus(treasury).plus(unclassified);
  return {
    drawer: roundMoney(drawer),
    treasury: roundMoney(treasury),
    unclassified: roundMoney(unclassified),
    total: roundMoney(total)
  };
}

export function sumDrawerExpensesForDay(entries: CashbookEntryRecord[], date: string): string {
  return sumExpensesByFundingForDay(entries, date).drawer;
}

export function computeDailySalesFromDrawer(
  drawerCount: string,
  drawerExpenseSum: string
): string {
  return roundMoney(toMoney(drawerCount).plus(toMoney(drawerExpenseSum)));
}

/** Income amount for a new drawer line (expenses only on the first line of the day). */
export function amountForNewDrawerSalesLine(
  entries: CashbookEntryRecord[],
  date: string,
  drawerCount: string
): string {
  const existing = findActiveDrawerSalesEntriesForDay(entries, date);
  if (existing.length === 0) {
    return computeDailySalesFromDrawer(drawerCount, sumDrawerExpensesForDay(entries, date));
  }
  return roundMoney(drawerCount);
}

/**
 * Reconcile all drawer sales line amounts for a day after expense changes.
 * Earliest (by id) hosts expenses once; others = their drawerCount only.
 */
export function reconcileDrawerSalesAmountsForDay(
  entries: CashbookEntryRecord[],
  date: string
): { entryId: string; amount: string }[] {
  const drawerSales = findActiveDrawerSalesEntriesForDay(entries, date);
  if (drawerSales.length === 0) return [];
  const expenses = sumDrawerExpensesForDay(entries, date);
  return drawerSales.map((s, i) => ({
    entryId: s.id,
    amount:
      i === 0
        ? computeDailySalesFromDrawer(s.drawerCount ?? '0.00', expenses)
        : roundMoney(s.drawerCount ?? '0.00')
  }));
}

export type DaySalesChannelDecision =
  | { action: 'ok' }
  | { action: 'block'; reason: 'payvox' | 'manual_sales' | 'manual_income' };

export function canSaveDrawerCount(
  entries: CashbookEntryRecord[],
  date: string
): DaySalesChannelDecision {
  const sales = findActiveSalesEntryForDay(entries, date);
  if (!sales) return { action: 'ok' };
  if (isActivePayVoxSalesEntry(sales) || sales.salesOrigin === 'payvox') {
    return { action: 'block', reason: 'payvox' };
  }
  // Multiple Path A (drawer) lines per day are allowed (e.g. morning + night shifts)
  if (sales.salesOrigin === 'drawer' || sales.drawerCount != null) {
    return { action: 'ok' };
  }
  // Legacy/CSV manual sales without drawer origin
  if (sales.categoryKey === 'sales') {
    return { action: 'block', reason: 'manual_sales' };
  }
  return { action: 'ok' };
}

export type PayVoxDayDecision =
  | { status: 'ok' }
  | { status: 'skip' }
  | { status: 'blocked'; reason: 'manual_income' };

export function canImportPayVoxDay(
  entries: CashbookEntryRecord[],
  date: string
): PayVoxDayDecision {
  const sales = findActiveSalesEntryForDay(entries, date);
  if (sales && (isActivePayVoxSalesEntry(sales) || sales.salesOrigin === 'payvox')) {
    return { status: 'skip' };
  }
  if (hasManualIncomeOnDay(entries, date)) {
    return { status: 'blocked', reason: 'manual_income' };
  }
  return { status: 'ok' };
}
